import './index.less';

import { ExclamationCircleFilled } from '@ant-design/icons';
import { Button, Input, Select, Tabs } from 'antd';
import { FC, useEffect, useMemo, useState } from 'react';

import { useUserInfo } from '@/hooks/useUserInfo';
import { useI18nContext } from '@/i18n/I18n';
import * as SecurityApi from '@/services/security';
import { ICountryListItem } from '@/services/types/security';
import { useUserStore } from '@/store/useUser';
import { complianceExtData } from '@/utils/complianceHelpers';
import { emailValidateNew, phoneValidate } from '@/utils/ruleValidate';

import { IEmailOrMobile } from '../../types';
import { useRetrieve } from '../../useRetrieve';

const { TabPane } = Tabs;

interface ICompsProps {
  nextStep: (params?: IEmailOrMobile) => void;
  risk_token: string;
  showSecurity: (info: IEmailOrMobile, risk_token: string) => void;
}
const Index: FC<ICompsProps> = ({ nextStep, risk_token, showSecurity }) => {
  const [$t, language] = useI18nContext();
  const [disabled, setDisabled] = useState(true);
  const [countryList, setCountryList] = useState<Array<ICountryListItem>>([]);
  const [inputText, setInputText] = useState<string>();
  const [email, setEmail] = useState<string>('');
  const [mobile, setMobile] = useState<string>('');
  const [countryCode, setCountryCode] = useState<string>();
  const [defaultCountryCode, setDefaultCountryCode] = useState('');
  const [currentKey, setCurrentKey] = useState<string>('1');
  const [mobileErr, setMobileErr] = useState<boolean>(false);
  const [emailErr, setEmailErr] = useState<boolean>(false);

  const { complianceConfig } = useUserStore();

  const { msgError } = useUserInfo();

  const { sendVerificationCode } = useRetrieve({}, risk_token);

  useEffect(() => {
    SecurityApi.getRegisterCountryCode().then((res) => {
      try {
        const { country_code } = JSON.parse(res as unknown as string);
        const defaultCountryCode =
          complianceExtData(complianceConfig?.DefaultCountryCode)?.default ||
          country_code;
        setDefaultCountryCode(defaultCountryCode);
        setCountryCode(defaultCountryCode);
      } catch {
        /* empty */
      }
    });
  }, []);

  useEffect(() => {
    // 获取国家列表
    SecurityApi.getAllListCountry()
      .then((res) => {
        const country_list = (res as ICountryListItem[]).filter(
          (country: ICountryListItem) => {
            if (country.code === 'MY') return true;
            return country.is_register;
          },
        );
        setCountryList(country_list);
      })
      .catch((error) => {
        msgError(error);
      });
  }, []);

  const filterSelections = useMemo(() => {
    return countryList.map((item: any) => ({
      ...item,
      value: item.code,
      label: `+${item.area_code} ${item[`name_${language}`] || item?.name_en}`,
      noPhoneLabel: `${item[`name_${language}`] || item?.name_en}`,
      countryName: item.name_en,
    }));
  }, [countryList]);

  const selections = useMemo(() => {
    if (!inputText) return filterSelections;

    const result = filterSelections.filter(
      (i) =>
        i.label.toUpperCase().includes(inputText.toUpperCase()) ||
        i.value.includes(inputText) ||
        i.countryName.toUpperCase().includes(inputText.toUpperCase()),
    );
    if (result.length) {
      return result;
    }
    return [];
  }, [inputText, filterSelections]);

  // tab切换
  const onChange = (key: string) => {
    setCurrentKey(key);

    setCountryCode(defaultCountryCode);

    setEmail('');
    setMobile('');
    setDisabled(true);
    setMobileErr(false);
    setEmailErr(false);
  };

  // 提示文案content
  const tip = () => {
    return (
      <div className="resetPasswordTip">
        <ExclamationCircleFilled className="tipIcon" />
        <div className="tipContext">{`${$t('Important')} ${$t(
          'withdrawalRestricted.changePassword',
        )}`}</div>
      </div>
    );
  };

  const countrySelect = (e: any) => {
    if (e) {
      if (mobile) {
        setDisabled(false);
      } else {
        setDisabled(true);
      }
      setCountryCode(e);
    } else {
      setDisabled(true);
      setCountryCode('');
    }
  };
  const mobileChange = (e: any) => {
    const mobile = e.target.value?.trim?.();
    if (mobile) {
      setMobile(mobile);
      if (phoneValidate(mobile)) {
        setMobileErr(false);
        if (countryCode) {
          setDisabled(false);
        } else {
          setDisabled(true);
        }
      } else {
        setMobileErr(true);
        setDisabled(true);
      }
    } else {
      setDisabled(true);
      setMobile('');
    }
  };

  const emailChange = (e: any) => {
    const email = e.target.value?.trim?.();
    if (email) {
      setEmail(email);

      if (emailValidateNew(email).status) {
        setEmailErr(false);
        setDisabled(false);
      } else {
        setEmailErr(true);
        setDisabled(true);
      }
    } else {
      setDisabled(true);
      setEmail('');
    }
  };

  // 点击确认
  const onConfirm = () => {
    if ((currentKey === '1' && emailErr) || (currentKey === '2' && mobileErr)) {
      return;
    }
    const info = {
      tapKey: currentKey,
      email,
      country_code: countryCode,
      mobile,
    };
    sendVerificationCode(info, (res: any) => {
      if (res?.risk_token) {
        showSecurity(info, res?.risk_token);
      } else {
        nextStep(info); // 打开第二步
      }
    });
  };

  return (
    <div className="emailOrMobileContainer">
      <Tabs defaultActiveKey={currentKey} onChange={onChange}>
        <TabPane tab={$t('Email')} key="1">
          {tip()}
          <div>
            <p className="lebal">{$t('Email')}</p>
            <Input
              autoComplete="off"
              value={email}
              placeholder={$t('Please enter your email address')}
              onChange={emailChange}
            />
            {emailErr && (
              <div className="inputErr">{$t(emailValidateNew(email).msg)}</div>
            )}
          </div>
        </TabPane>
        <TabPane tab={$t('Mobile')} key="2">
          {tip()}
          <div>
            <p className="lebal">{$t('Country')}</p>
            <Select
              disabled={
                !!complianceExtData(complianceConfig?.DefaultCountryCode)
                  ?.disabled
              }
              className="countrySelect"
              placeholder={$t('Please Select')}
              value={countryCode}
              defaultValue={defaultCountryCode}
              showSearch
              filterOption={(inputValue: string, options: any) => {
                const code =
                  options.children.props.children[1] +
                  options.children.props.children[2];
                return code.includes(inputValue);
              }}
              onChange={countrySelect}
            >
              {selections &&
                selections.map((item: any) => {
                  return (
                    <Select.Option key={item.code} value={item.code}>
                      <div className="selectItemContent">
                        <img
                          className="cht-w-[17px] cht-h-[17px]"
                          src={`/common-static/cht-static/user-svc/img/country/${item.countryName
                            .toLowerCase()
                            .replace(/\s/g, '-')}.svg`}
                          alt={''}
                          width="20"
                        />
                        &nbsp;+ {item.area_code}&nbsp;
                        {item[`name_${language === 'zh-MY' ? 'zh-CN' : 'en'}`]}
                      </div>
                    </Select.Option>
                  );
                })}
            </Select>
          </div>
          <div className="mobileContainer">
            <p className="lebal">{$t('Mobile')}</p>
            <Input
              value={mobile}
              autoComplete="off"
              readOnly={true}
              onFocus={(e: any) => e.target.removeAttribute('readonly')}
              placeholder={$t('Please enter your mobile number')}
              onChange={mobileChange}
            />
            {mobileErr && (
              <div className="inputErr">{$t('Invalid mobile number')}</div>
            )}
          </div>
        </TabPane>
      </Tabs>
      <Button
        type="primary"
        className="continueBtn"
        style={{ marginTop: 24 }}
        onClick={onConfirm}
        disabled={disabled}
      >
        {$t('Next')}
      </Button>
    </div>
  );
};
export default Index;
